import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { todayISO, formatEUR, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

/**
 * mode: 'quote' | 'invoice'
 */
export default function DocumentForm({ mode }) {
  const isInvoice = mode === "invoice";
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const clientPreselect = params.get("client");

  const [clients, setClients] = useState([]);
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    client_id: clientPreselect || "",
    issue_date: todayISO(),
    due_date: "",
    valid_until: "",
    notes: "",
    irpf_pct: 0,
    global_discount_pct: 0,
    lines: [
      { description: "", quantity: 1, unit_price: 0, discount_pct: 0, iva_pct: 21 },
    ],
    send_to_client: false,
    send_to_gestoria: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/clients"), api.get("/auth/me")]).then(([c, u]) => {
      setClients(c.data);
      setUser(u.data);
      setForm((f) => ({
        ...f,
        irpf_pct: u.data.default_irpf ?? 0,
      }));
      // apply user default IVA to line if 0
      setForm((f) => ({
        ...f,
        lines: f.lines.map((l) => ({ ...l, iva_pct: u.data.default_iva ?? 21 })),
      }));
    });
  }, []);

  const setField = (k) => (val) => setForm((f) => ({ ...f, [k]: val }));

  const setLine = (i, k, val) =>
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, idx) => (idx === i ? { ...l, [k]: val } : l)),
    }));

  const addLine = () =>
    setForm((f) => ({
      ...f,
      lines: [
        ...f.lines,
        {
          description: "",
          quantity: 1,
          unit_price: 0,
          discount_pct: 0,
          iva_pct: user?.default_iva ?? 21,
        },
      ],
    }));

  const removeLine = (i) =>
    setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const totals = useMemo(() => {
    let subtotalBase = 0;
    let disc = 0;
    let iva = 0;
    for (const l of form.lines) {
      const gross = (l.quantity || 0) * (l.unit_price || 0);
      const lineDisc = gross * ((l.discount_pct || 0) / 100);
      const base = gross - lineDisc;
      disc += lineDisc;
      subtotalBase += base;
    }
    const globalDisc = subtotalBase * ((form.global_discount_pct || 0) / 100);
    disc += globalDisc;
    let subtotal = 0;
    for (const l of form.lines) {
      const gross = (l.quantity || 0) * (l.unit_price || 0);
      const base = gross - gross * ((l.discount_pct || 0) / 100);
      const share = subtotalBase === 0 ? 0 : base / subtotalBase;
      const adj = base - globalDisc * share;
      iva += adj * ((l.iva_pct || 0) / 100);
      subtotal += adj;
    }
    const irpf = subtotal * ((form.irpf_pct || 0) / 100);
    return {
      subtotal: subtotal,
      discount: disc,
      iva,
      irpf,
      total: subtotal + iva - irpf,
    };
  }, [form.lines, form.irpf_pct, form.global_discount_pct]);

  const submit = async () => {
    if (!form.client_id) {
      toast.error("Selecciona un cliente");
      return;
    }
    if (form.lines.length === 0 || !form.lines[0].description) {
      toast.error("Añade al menos una línea de concepto");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        client_id: form.client_id,
        issue_date: form.issue_date,
        notes: form.notes,
        irpf_pct: Number(form.irpf_pct) || 0,
        global_discount_pct: Number(form.global_discount_pct) || 0,
        lines: form.lines.map((l) => ({
          description: l.description,
          quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0,
          discount_pct: Number(l.discount_pct) || 0,
          iva_pct: Number(l.iva_pct) || 0,
        })),
      };
      if (isInvoice) {
        payload.due_date = form.due_date || null;
        payload.send_to_client = form.send_to_client;
        payload.send_to_gestoria = form.send_to_gestoria;
        const { data } = await api.post("/invoices", payload);
        toast.success(`Factura ${data.number} creada`);
        if (data.sent_client) toast.success("Enviada al cliente");
        if (data.sent_gestoria) toast.success("Enviada a la gestoría");
        navigate("/app/facturas");
      } else {
        payload.valid_until = form.valid_until || null;
        const { data } = await api.post("/quotes", payload);
        toast.success(`Presupuesto ${data.number} creado`);
        navigate("/app/presupuestos");
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid={`${mode}-form-page`}>
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
          {isInvoice ? "Nueva factura" : "Nuevo presupuesto"}
        </h1>
      </div>

      <Card className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Cliente *</Label>
            <Select value={form.client_id} onValueChange={setField("client_id")}>
              <SelectTrigger className="mt-1.5" data-testid="doc-client-select">
                <SelectValue placeholder="Selecciona cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} {c.nif ? `(${c.nif})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Fecha de emisión</Label>
            <Input
              type="date"
              value={form.issue_date}
              onChange={(e) => setField("issue_date")(e.target.value)}
              className="mt-1.5"
            />
          </div>
          {isInvoice ? (
            <div>
              <Label>Vencimiento</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setField("due_date")(e.target.value)}
                className="mt-1.5"
              />
            </div>
          ) : (
            <div>
              <Label>Válido hasta</Label>
              <Input
                type="date"
                value={form.valid_until}
                onChange={(e) => setField("valid_until")(e.target.value)}
                className="mt-1.5"
              />
            </div>
          )}
        </div>
      </Card>

      {/* Lines */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg">Conceptos</h2>
          <Button
            size="sm"
            variant="outline"
            onClick={addLine}
            data-testid="add-line-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Añadir línea
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left py-2 pr-2">Descripción</th>
                <th className="text-right py-2 pr-2 w-20">Cant.</th>
                <th className="text-right py-2 pr-2 w-28">Precio</th>
                <th className="text-right py-2 pr-2 w-20">Dto %</th>
                <th className="text-right py-2 pr-2 w-20">IVA %</th>
                <th className="text-right py-2 pr-2 w-28">Importe</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {form.lines.map((l, i) => {
                const gross = (l.quantity || 0) * (l.unit_price || 0);
                const base = gross - gross * ((l.discount_pct || 0) / 100);
                return (
                  <tr
                    key={i}
                    className="border-b border-slate-100"
                    data-testid={`line-row-${i}`}
                  >
                    <td className="py-2 pr-2">
                      <Input
                        value={l.description}
                        onChange={(e) => setLine(i, "description", e.target.value)}
                        placeholder="Servicio o producto"
                        data-testid={`line-desc-${i}`}
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={l.quantity}
                        onChange={(e) => setLine(i, "quantity", e.target.value)}
                        className="text-right"
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={l.unit_price}
                        onChange={(e) => setLine(i, "unit_price", e.target.value)}
                        className="text-right"
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={l.discount_pct}
                        onChange={(e) => setLine(i, "discount_pct", e.target.value)}
                        className="text-right"
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={l.iva_pct}
                        onChange={(e) => setLine(i, "iva_pct", e.target.value)}
                        className="text-right"
                      />
                    </td>
                    <td className="py-2 pr-2 text-right font-medium">
                      {formatEUR(base)}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeLine(i)}
                        disabled={form.lines.length === 1}
                      >
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div>
              <Label>Notas</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setField("notes")(e.target.value)}
                rows={3}
                placeholder="Notas o condiciones..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Descuento global %</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.global_discount_pct}
                  onChange={(e) => setField("global_discount_pct")(e.target.value)}
                />
              </div>
              <div>
                <Label>IRPF %</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.irpf_pct}
                  onChange={(e) => setField("irpf_pct")(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <div className="flex justify-between py-1 text-sm text-slate-600">
              <span>Subtotal (base imponible)</span>
              <span data-testid="totals-subtotal">{formatEUR(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between py-1 text-sm text-slate-600">
              <span>Descuentos</span>
              <span>-{formatEUR(totals.discount)}</span>
            </div>
            <div className="flex justify-between py-1 text-sm text-slate-600">
              <span>IVA</span>
              <span data-testid="totals-iva">{formatEUR(totals.iva)}</span>
            </div>
            {totals.irpf > 0 && (
              <div className="flex justify-between py-1 text-sm text-slate-600">
                <span>IRPF ({form.irpf_pct}%)</span>
                <span>-{formatEUR(totals.irpf)}</span>
              </div>
            )}
            <div className="border-t border-slate-300 mt-2 pt-2 flex justify-between font-display font-bold text-lg">
              <span className="text-slate-900">TOTAL</span>
              <span className="text-blue-600" data-testid="totals-total">
                {formatEUR(totals.total)}
              </span>
            </div>
          </div>
        </div>

        {isInvoice && (
          <div className="mt-6 space-y-3 pt-4 border-t border-slate-200">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.send_to_client}
                onCheckedChange={(v) => setField("send_to_client")(v === true)}
                data-testid="send-client-checkbox"
              />
              Enviar por email al cliente al crear (adjunta PDF + XML)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.send_to_gestoria}
                onCheckedChange={(v) => setField("send_to_gestoria")(v === true)}
                disabled={!user?.gestoria_email}
                data-testid="send-gestoria-checkbox"
              />
              Enviar copia a la gestoría{" "}
              {!user?.gestoria_email && (
                <span className="text-xs text-rose-500">
                  (configura primero el email en Configuración)
                </span>
              )}
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => navigate(-1)}>
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="doc-submit-btn"
          >
            {submitting
              ? "Guardando..."
              : isInvoice
                ? "Crear factura"
                : "Crear presupuesto"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
